import * as React from "react";

import { TABLECELL_PENCIL_CLICK } from "../../metricsAction";
import { MetricsActionPayload, trackAction } from "../../Internals/Metrics/MetricsProvider";
import { AnyContext, nullElement } from "../../TypingUtils";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { noop } from "../../../../../Common/TypingUtils";

import { TableCellPencil } from "./TableCellPencil/TableCellPencil";
import { renderChildrenWithIndent } from "./TableUtils";

function groupSelector(props: TableCellProps): MetricsActionPayload {
    return { group: props.groupKey ?? "" };
}

export interface TableCellProps {
    hiddenGroup: string;
    bindingPath: string;
    firstDependencyPath: undefined | AbsoluteModelPath;
    children: React.ReactNode;
    filled: boolean;
    groupType?: string;
    grouped: boolean;
    groupKey: string;
    groupText: JSX.Element | string;
    isHide: boolean;
    onHideColGrouping: () => void;
    onHideColumns: () => void;
    onShowColGrouping: (doNotThrottle?: boolean) => void;
    onShowColumns: (doNotThrottle?: boolean) => void;
}

interface TableCellState {
    hide: boolean;
}

export class TableCell extends React.Component<TableCellProps, TableCellState> {
    public static defaultProps = {
        groupText: "",
        groupKey: "",
        isHide: false,
        onHideColGrouping: noop,
        onHideColumns: noop,
        onShowColumns: noop,
        onShowColGrouping: noop,
    };

    public constructor(props: TableCellProps, context: AnyContext) {
        super(props, context);

        this.state = { hide: this.props.groupType === "pencil" && !this.props.filled };
    }

    public componentDidMount(): void {
        if (this.props.filled) {
            this.props.onShowColumns();
            this.props.onShowColGrouping();
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: TableCellProps): void {
        if (this.props.bindingPath !== nextProps.bindingPath) {
            if (
                (this.props.groupType === "pencil" && !this.state.hide) ||
                (this.props.groupType === "data" && this.props.filled)
            ) {
                this.props.onHideColumns();
                this.props.onHideColGrouping();
            }

            this.setState({ hide: nextProps.groupType === "pencil" && !nextProps.filled });

            if (nextProps.filled) {
                this.props.onShowColumns();
                this.props.onShowColGrouping();
            }
        }
    }

    public componentWillUnmount(): void {
        if (
            (this.props.groupType === "pencil" && !this.state.hide) ||
            (this.props.groupType === "data" && this.props.filled)
        ) {
            this.props.onHideColumns();
            this.props.onHideColGrouping();
        }
    }

    public render(): React.ReactNode {
        const { groupKey } = this.props;
        let result;
        if (this.props.isHide) {
            result = nullElement;
        } else {
            const isHideContent = this.state.hide && !this.props.filled;
            if (!(isHideContent || this.props.grouped)) {
                result = renderChildrenWithIndent(this.props.children);
            } else {
                switch (this.props.groupType) {
                    case "pencil":
                        result = (
                            <TableCellPencil data-tid={groupKey} groupKey={groupKey} onClick={this.handleOnClick} />
                        );
                        break;
                    case "text":
                        result = <span>{this.props.groupText}</span>;
                        break;
                    case "data":
                        result = this.props.children;
                        break;
                    default:
                        result = nullElement;
                        break;
                }
            }
        }
        return (
            <TestPageObjectHandle
                id={`${this.props.firstDependencyPath?.toLegacyPath()}/${groupKey}`}
                pageObject={{
                    getType: () => "TableCell",
                    click: this.handleOnClick,
                    isHidden: () => this.state.hide,
                }}>
                {result}
            </TestPageObjectHandle>
        );
    }

    @trackAction(TABLECELL_PENCIL_CLICK, groupSelector)
    public readonly handleOnClick = (): void => {
        this.setState({ hide: false });
        this.props.onShowColumns(true);
        this.props.onShowColGrouping(true);
    };
}
